import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertModal } from "@/components/modal/alert-modal";
import {
  Eye,
  MoreHorizontal,
  Pencil,
  Trash2,
  Check,
  Users,
} from "lucide-react";
import { showToast } from "@/components/ui/show-toast";
import { useNavigate } from "react-router-dom";
import {
  useDeleteOpportunity,
  useUpdateOpportunityStatus,
} from "@/services/opportunities/queries";
import { useAuthStore } from "@/store/auth";
import type { Opportunity } from "@/services/types";
import { usePostHog } from "posthog-js/react";

interface CellActionProps {
  data: Opportunity;
  activeOrganisationId?: string;
}

export const CellAction: React.FC<CellActionProps> = ({
  data,
  activeOrganisationId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { user, context } = useAuthStore();

  // Check if the opportunity belongs to the user's organisation
  const opportunityOrgId = data.ownerEntity?.id || null;
  const isOwnOpportunity =
    activeOrganisationId && opportunityOrgId === activeOrganisationId;

  // Check if current user is an admin
  const isAdmin = useMemo(() => {
    if (context === 'organisation') {
      return user?.organisation?.role === 'org_admin';
    } else if (context === 'workspace') {
      return user?.workspace?.role === 'workspace_admin';
    }
    return false;
  }, [context, user]);

  // Show action buttons if user owns the opportunity OR is an admin
  const canManageOpportunity = isOwnOpportunity || isAdmin;

  const { mutate: deleteOpportunity, isPending: isDeleting } =
    useDeleteOpportunity(
      () => {
        showToast(
          "success",
          `Opportunity "${data.title}" deleted successfully`
        );
        setShowDeleteModal(false);
      },
      (error: any) => {
        showToast(
          "error",
          error?.response?.data?.message ||
            "Failed to delete opportunity. Please try again."
        );
        console.error("Delete opportunity error:", error);
      }
    );

  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateOpportunityStatus(
      () => {
        showToast("success", "Opportunity status updated successfully");
      },
      (error: any) => {
        showToast(
          "error",
          error?.response?.data?.message ||
            "Failed to update opportunity status. Please try again."
        );
        console.error("Update status error:", error);
      }
    );

  const handleView = () => {
    posthog?.capture('opportunity_viewed', { opportunity_id: data.id, opportunity_title: data.title });
    navigate(`/dashboard/opportunities/${data.id}`);
  };

  const handleEdit = () => {
    posthog?.capture('opportunity_edit_clicked', { opportunity_id: data.id, opportunity_title: data.title });
    navigate(`/dashboard/opportunities/${data.id}/edit`);
  };

  const handleDelete = () => {
    posthog?.capture('opportunity_deleted', { opportunity_id: data.id, opportunity_title: data.title });
    deleteOpportunity(data.id);
  };

  const handleStatusChange = (newStatus: string) => {
    if (data.status === newStatus) return;
    posthog?.capture('opportunity_status_changed', { opportunity_id: data.id, from: data.status, to: newStatus });
    updateStatus({ id: data.id, status: newStatus });
  };

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
  ];

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>

            <DropdownMenuItem onClick={handleView}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>

            {canManageOpportunity && (
              <>
                <DropdownMenuItem onClick={handleEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Opportunity
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() =>
                    navigate(`/dashboard/opportunities/${data.id}/applications`)
                  }
                >
                  <Users className="mr-2 h-4 w-4" />
                  View Applications
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger disabled={isUpdatingStatus}>
                    <span>Update Status</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {statusOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        disabled={
                          isUpdatingStatus || data.status === option.value
                        }
                      >
                        {data.status === option.value ? (
                          <Check className="mr-2 h-4 w-4" />
                        ) : (
                          <span className="mr-2 h-4 w-4" />
                        )}
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => setShowDeleteModal(true)}
                  className="text-red-600"
                  disabled={isDeleting}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Opportunity
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title="Delete Opportunity"
        description={`Are you sure you want to delete "${data.title}"? This action cannot be undone.`}
      />
    </>
  );
};
